import { useEffect, useMemo, useState, type ReactNode } from 'react';
import i18next from 'i18next';
import type { TenantConfig } from '@pink/shared';

import {
  ConfigFetchError,
  ConfigFetchErrorKind,
  fetchTenantConfig,
} from './fetch-tenant-config.js';
import {
  TenantConfigContext,
  type BootstrapState,
  type TenantConfigContextValue,
} from './use-tenant-config.js';
import { applyTheme } from '../theming-module/apply-theme.js';
import { applyTenantFavicon } from '../theming-module/apply-favicon.js';
import { applyTenantTextOverrides } from '../i18n-module/apply-text-overrides.js';
import { MOCK_TENANT_CONFIG } from '../dev-module/mock-config.js';

/**
 * Props für `<ConfigProvider>`.
 *
 * Das Loader-Predicate ist injizierbar, damit Tests die Mock-Mode-Logik
 * deterministisch ansteuern können, ohne `import.meta.env` zu mocken.
 */
interface ConfigProviderProps {
  readonly children: ReactNode;
  readonly skeleton: ReactNode;
  readonly isMockMode?: boolean;
  readonly loadConfig?: (signal: AbortSignal) => Promise<TenantConfig>;
}

/**
 * Liefert den Default-Loader: Mock-Mode → hartkodierte Config,
 * sonst Fetch gegen `/api/config.json`.
 *
 * @param isMockMode Ob das Mock-Bundle statt der Live-Config gezogen werden soll.
 */
function defaultLoader(isMockMode: boolean): (signal: AbortSignal) => Promise<TenantConfig> {
  if (isMockMode) {
    return () => Promise.resolve(MOCK_TENANT_CONFIG);
  }
  return (signal: AbortSignal) => fetchTenantConfig(signal);
}

/**
 * Bestimmt aus einem unbekannten Catch-Wert die passenden Bootstrap-Error-
 * Felder. `ConfigFetchError` liefert eine genaue Diskriminanten, alle
 * anderen Fehler werden generisch als `network` eingestuft.
 *
 * @param cause Geworfener Wert aus dem Loader.
 */
function toBootstrapError(cause: unknown): BootstrapState {
  if (cause instanceof ConfigFetchError) {
    return { status: 'error', errorKind: cause.kind, message: cause.message };
  }
  const message = cause instanceof Error ? cause.message : 'Unbekannter Bootstrap-Fehler.';
  return { status: 'error', errorKind: ConfigFetchErrorKind.Network, message };
}

/**
 * Top-Level-Provider: fetched einmalig die Tenant-Config, hält sie in
 * Memory, setzt Theme-Variablen ans `:root` und stellt den Bootstrap-Status
 * über den Context bereit.
 *
 * Solange der Fetch läuft, rendert er den `skeleton`-Slot (Bootstrap-Skeleton,
 * Vorgabe AFO-005). Bei Fehlern wechselt der Status auf `error`; das
 * vollständige Mapping auf den Technical-Error-Screen passiert in DEV-016 —
 * hier reicht ein roher Fehler-Block.
 */
export function ConfigProvider(props: ConfigProviderProps): JSX.Element {
  const { children, skeleton, isMockMode = false, loadConfig } = props;
  const [bootstrap, setBootstrap] = useState<BootstrapState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    const loader = loadConfig ?? defaultLoader(isMockMode);
    loader(controller.signal).then(
      (config) => {
        if (controller.signal.aborted) {
          return;
        }
        applyTheme(config.themeColors, document.documentElement);
        applyTenantFavicon({
          tenantName: config.tenantName,
          primaryColor: config.themeColors.primary,
          letterColor: config.themeColors.textOnPrimary,
        });
        applyTenantTextOverrides(i18next, config.textOverrides);
        setBootstrap({ status: 'ready', config });
      },
      (cause: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setBootstrap(toBootstrapError(cause));
      },
    );
    return () => controller.abort();
  }, [isMockMode, loadConfig]);

  const value: TenantConfigContextValue = useMemo(() => ({ bootstrap }), [bootstrap]);

  return (
    <TenantConfigContext.Provider value={value}>
      {bootstrap.status === 'loading' ? skeleton : children}
    </TenantConfigContext.Provider>
  );
}
